import * as yup from 'yup';
import * as formSchemaUtils from './formSchemaUtils';
import { getChemicalProductObjectSchema } from './ChemicalProductDetailsFormSchema';
import getChemicalRiskAttributesObjectSchema from './ChemicalRiskAttributesDetailsFormSchema';
import { conditionToDisplayEnvInfoAndEnvRiskLevel } from '../utils';
import keycloak from '../keycloak';

const getInventoryDetailsFormSchema = () =>
  yup.object().shape({
    outOfBuilding: yup
      .mixed()
      .outOfBuildingRequiredValidation(yup.ref('location.locationInfo'))
      .outOfBuildingErrorValidation(yup.ref('location.locationInfo'))
      .nullable(),
    location: formSchemaUtils.objectRequired(),
    checkCseComments: yup
      .string()
      .max(4000, formSchemaUtils.EXCEEDS_CHECK_CSE_COMMENTS_SIZE)
      .nullable(),
    remarks: yup.string().max(4000, formSchemaUtils.EXCEEDS_REMARKS_SIZE).nullable(),
    technicalEquipment: yup
      .string()
      .max(200, formSchemaUtils.EXCEEDS_TECHNICAL_EQUIPMENT_SIZE)
      .nullable(),
    preciseLocation: yup
      .mixed()
      .gisRiskValidation(
        yup.ref('id'),
        yup.ref('location.locationInfo'),
        yup.ref('location.surface'),
        yup.ref('chemicalProduct.hazards'),
        keycloak
      )
      .nullable(),
    contacts: yup.mixed().test('contacts', formSchemaUtils.LIST_CANNOT_BE_EMPTY, value => {
      return (
        value &&
        value.length > 0 &&
        value.filter(v => !v['wasDeleted'] && (!v['delDte'] || new Date(v['delDte']) > new Date()))
          .length > 0
      );
    }),
    chemicalProduct: getChemicalProductObjectSchema().shape({
      selectedChemicalProduct: formSchemaUtils.objectWithIdRequired(),
      tradeName: yup.mixed().when('selectedChemicalProduct.id', {
        is: value => value == -1,
        then: yup
          .string()
          .max(200, formSchemaUtils.EXCEEDS_TRADE_NAME_SIZE)
          .tradeNameValidation([null, yup.ref('supplier'), yup.ref('purity')], keycloak)
          .mixedCustomRequired()
          .nullable(),
      }),
    }),
    chemicalRiskAttributes: getChemicalRiskAttributesObjectSchema().shape({
      boilingPoint: yup
        .number()
        .min(-99999999999999999, formSchemaUtils.VALUE_EXCEEDS_MAX_17)
        .max(99999999999999999, formSchemaUtils.VALUE_EXCEEDS_MAX_17)
        .when(['chemicalProductStateId', 'hasChemRiskCalculation'], {
          is: (chemicalProductStateId, hasChemRiskCalculation) =>
            chemicalProductStateId === 2 && hasChemRiskCalculation, //LIQUID
          then: yup.number().required(formSchemaUtils.VALUE_IS_REQUIRED).nullable(),
        })
        .nullable(),

      operatingTemperature: yup
        .number()
        .min(-99999999999999999, formSchemaUtils.VALUE_EXCEEDS_MAX_17)
        .max(99999999999999999, formSchemaUtils.VALUE_EXCEEDS_MAX_17)
        .when(['chemicalProductStateId', 'hasChemRiskCalculation'], {
          is: (chemicalProductStateId, hasChemRiskCalculation) =>
            chemicalProductStateId === 2 && hasChemRiskCalculation, //LIQUID
          then: yup.number().required(formSchemaUtils.VALUE_IS_REQUIRED).nullable(),
        })
        .nullable(),
      exposureMonitoringDesc: yup
        .string()
        .max(1000, formSchemaUtils.EXCEEDS_EXPOSURE_MONITORING_DESC_SIZE)
        .nullable(),
      //edmsNumbersExposureMonitoring:
      providedDetails: yup
        .string()
        .max(2000, formSchemaUtils.EXCEEDS_PROVIDED_DETAILS_SIZE)
        .nullable(),
      precautionsAndEmergencyProcedures: yup
        .string()
        .max(4000, formSchemaUtils.EXCEEDS_EMERGENCY_PROCEDURES_SIZE)
        .nullable(),
    }),
    orgUnit: yup.object({
      name: yup.mixed().mixedCustomRequired(),
    }),
    quantity: yup
      .number()
      .min(0, formSchemaUtils.VALUE_MUST_BE_POSITIVE_INTEGER)
      .max(99999999, formSchemaUtils.VALUE_EXCEEDS_MAX)
      .required(formSchemaUtils.VALUE_IS_REQUIRED)
      .nullable(),
    files: yup
      .array()
      .of(
        yup.object().shape({
          format: yup
            .mixed()
            .test('format', formSchemaUtils.UNSUPPORTED_FILE_FORMAT, function (v) {
              return (
                this.parent &&
                (this.parent.fileData.id ||
                  (this.parent.fileData.format &&
                    formSchemaUtils.INVENTORY_FILE_SUPPORTED_FORMATS.includes(
                      this.parent.fileData.format
                    )))
              );
            })
            .nullable(),
          size: yup
            .mixed()
            .test('size', formSchemaUtils.EXCEEDS_FILE_SIZE_LIMIT, function (v) {
              return (
                this.parent &&
                (this.parent.fileData.id ||
                  !this.parent.fileData.file ||
                  this.parent.fileData.file.size <= formSchemaUtils.FILE_SIZE_LIMIT)
              );
            })
            .nullable(),
        })
      )
      .nullable(),
    envAttributes: yup
      .object()
      .when(['quantity', 'chemicalProduct'], {
        is: (quantity, chemicalProduct) =>
          conditionToDisplayEnvInfoAndEnvRiskLevel({ chemicalProduct, quantity }),
        then: yup.object({
          typePresence: formSchemaUtils.objectWithIdRequired(),
          waterCourse: formSchemaUtils.objectWithIdRequired(),
          containerInspection: formSchemaUtils.objectWithIdRequired(),
          pipingInspection: formSchemaUtils.objectWithIdRequired(),
          procLoadUnload: formSchemaUtils.objectWithIdRequired(),
          adequacyReten: formSchemaUtils.objectWithIdRequired(),
          testRetenTightness: formSchemaUtils.objectWithIdRequired(),
          leakDetectInspection: formSchemaUtils.objectWithIdRequired(),
          overflowDetectInspection: formSchemaUtils.objectWithIdRequired(),
          adequacySeparBufTank: formSchemaUtils.objectWithIdRequired(),
          separBufTankInspection: formSchemaUtils.objectWithIdRequired(),
          spillKit: formSchemaUtils.objectWithIdRequired(),
        }),
      })
      .nullable(),
    checkCseUser: yup.object().requiredIfRefHasValue(yup.ref('checkCseDte')).nullable(),
    checkCseDte: yup
      .object()
      .requiredIfRefHasValue(yup.ref('checkCseUser'))
      .isSameOrBeforeToday()
      .nullable(),
  });

export default getInventoryDetailsFormSchema;
